import TabTrap from '../../core/utils/tab-trap';

const MegaMenu = (header, selectors, attributes) => {
  const root = document.documentElement;
  const { megaMenuContainer, megaMenuTriggers, megaMenuTargets, mobileMegaMenuContainer, mobileMegaMenuTriggers, mobileMegaMenuTargets, submenuTriggers } = selectors;
  const { megaMenuOpen, megaMenuShow, megaMenuActive, submenuOpen, menuFocused } = attributes;

  const headerInner = header.querySelector('.header__inner');
  const megaMenus = [
    header.querySelector(megaMenuContainer),
    header.querySelector(mobileMegaMenuContainer)
  ];
  const megaTriggers = [
    ...header.querySelectorAll(megaMenuTriggers),
    ...header.querySelectorAll(mobileMegaMenuTriggers)
  ];
  const megaTargets = [
    ...header.querySelectorAll(megaMenuTargets),
    ...header.querySelectorAll(mobileMegaMenuTargets)
  ];
  const subTriggers = [
    ...header.querySelectorAll(submenuTriggers)];

  if (!megaTriggers.length || !megaTargets.length || !megaMenus.length) {
    throw new Error('MegaMenu: Required elements not found');
  }

  const DELAY = headerInner.dataset.delayHide || 300;

  const initialState = {
    isOpen: false,
    currentTrigger: null,
    previousTrigger: null,
    currentTarget: null,
    previousTarget: null,
    submenuOpen: false,
    previousSubmenu: null,
  };

  const toggleMegaMenu = (isClosed, tabTrap) => {
    state.isOpen = !isClosed;

    if (tabTrap) {
      triggerMap.forEach(({ tabTrap }) => {
        tabTrap.off();
      })
      if (state.isOpen) {
        tabTrap.on();
      } else {
        tabTrap.off();
      }
    }

    header.toggleAttribute(megaMenuOpen, state.isOpen);

    if (state.isOpen) {
      root.style.overflow = 'hidden';
      state.currentTrigger.setAttribute(megaMenuActive, '');
      state.currentTrigger.setAttribute(menuFocused, '');
      state.currentTarget.setAttribute(megaMenuShow, '');

      state.previousTrigger?.removeAttribute(megaMenuActive);
      state.previousTrigger?.removeAttribute(menuFocused);
      state.previousTarget?.removeAttribute(megaMenuShow);

      // Reset submenu state when opening mega menu
      if (state.previousSubmenu) {
        state.previousSubmenu.removeAttribute(submenuOpen);
      }
      state.submenuOpen = false;
      state.previousSubmenu = null;
    } else {
      root.style.overflow = '';

      setTimeout(() => {
        for (const item of triggerMap) {
          item.trigger.removeAttribute(megaMenuActive);
          item.trigger.removeAttribute(menuFocused);
          item.targetContent.removeAttribute(megaMenuShow);
        }
      }, DELAY);

      state = { ...initialState };

      window.addEventListener('scroll', handleScroll);
    }
  };

  const onSubmenuClick = event => {
    event.preventDefault();
    const target = event.currentTarget;
    const clickedElement = event.target;

    if (state.isOpen) {
      return;
    }

    const isChildOfCurrentTarget = target.contains(clickedElement) && target !== clickedElement;

    if (isChildOfCurrentTarget && clickedElement !== event.currentTarget) {
      window.location.href = clickedElement.href;
      return;
    }

    if (state.previousSubmenu && state.previousSubmenu !== target) {
      state.previousSubmenu.removeAttribute(menuFocused);
      state.previousSubmenu.removeAttribute(submenuOpen);
      state.submenuOpen = false;
    }

    if (state.previousSubmenu === target) {
      state.submenuOpen = !state.submenuOpen;
    } else {
      state.submenuOpen = true;
    }

    state.previousSubmenu = target;

    if (state.submenuOpen) {
      target.setAttribute(submenuOpen, '');
      target.setAttribute(menuFocused, '');
    } else {
      target.removeAttribute(submenuOpen);
      target.removeAttribute(menuFocused);
    }
  };

  const resetSubmenus = () => {
    if (state.previousSubmenu) {
      state.previousSubmenu.removeAttribute(submenuOpen);
    }
    state.submenuOpen = false;
    state.previousSubmenu = null;
  };

  const handleScroll = () => {
    resetSubmenus();
    window.removeEventListener('scroll', handleScroll);
  };

  const handleClickOutside = event => {
    if (state.submenuOpen && !state.isOpen) {
      const isClickInsideSubmenu = subTriggers.some(item => item === event.target || item.contains(event.target));
      const isClickOnPreviousSubmenu = state.previousSubmenu === event.target || state.previousSubmenu.contains(event.target);

      if (!isClickInsideSubmenu && !isClickOnPreviousSubmenu) {
        state.previousSubmenu.removeAttribute(menuFocused);
        state.previousSubmenu.removeAttribute(submenuOpen);
        state.submenuOpen = false;
        state.previousSubmenu = null;
      }
    }
  };

  const handleModalTrigger = (event, trigger, target, tabTrap) => {
    event.preventDefault();
    const isSameTrigger = state.currentTrigger === trigger;

    state = {
      ...state,
      currentTrigger: trigger,
      previousTrigger: state.currentTrigger,
      currentTarget: target,
      previousTarget: state.currentTarget,
    };

    toggleMegaMenu(isSameTrigger, tabTrap);
  };

  const attachEventListeners = () => {
    subTriggers.forEach(item => {
      item.addEventListener('click', event => {
        if (state.isOpen) {
          toggleMegaMenu(true);
        }
        onSubmenuClick(event);
      });
    });

    triggerMap.forEach(({ trigger, targetContent, tabTrap }) => {
      trigger.addEventListener('click', event => handleModalTrigger(event, trigger, targetContent, tabTrap));
    });

    document.addEventListener('click', event => {
      handleClickOutside(event);

      if (state.isOpen && !megaTriggers.some(trigger => trigger.contains(event.target)) && !megaMenus.some(menu => menu.contains(event.target)) && !subTriggers.some(trigger => trigger.contains(event.target))) {
        toggleMegaMenu(true);
      }
    });
  };

  const createTriggerMap = () => {
    return megaTriggers.reduce((acc, trigger) => {
      const targetId = trigger.dataset.menuTarget;
      const targetContext = trigger.dataset.menuTriggerContext;
      const targetContent = megaTargets.find(target => target.dataset.menuId === targetId && target.dataset.menuContentContext === targetContext);

      if (!targetContent) {
        throw new Error(`MegaMenu: Target content not found for trigger ${targetId}`);
      }

      const tabTrap = new TabTrap(targetContent);
      tabTrap.ele.addEventListener('tabtrapEscaped', () => {
        toggleMegaMenu(true, tabTrap);
      })

      return [...acc, { trigger, targetContent, tabTrap }];
    }, []);
  };

  let state = { ...initialState };
  let triggerMap = [];

  const init = () => {
    triggerMap = createTriggerMap();
    attachEventListeners();
  };

  return { init, handleScroll, closeMegaMenu: () => toggleMegaMenu(true) };
};

export default MegaMenu;
